// Recruiting MCP servers compared. Every cell comes from the vendor's own page (see `source`),
// read on MCP_COMPARISON_REVIEWED. "Not stated" means the page we read did not say — not "no".
// Only official, vendor-run servers are listed; community and aggregator servers are excluded.

export const MCP_COMPARISON_REVIEWED = '2026-09-19';
export const MCP_COMPARISON_REVIEWED_LABEL = '19 September 2026';
/** Latest re-check of any row (Greenhouse, Ashby and Workable were re-read on this date). */
export const MCP_COMPARISON_UPDATED = '2026-10-01';
export const MCP_COMPARISON_UPDATED_LABEL = '1 October 2026';

export type McpCategory = 'ATS' | 'Agency CRM' | 'Screening & interviews';

export type McpVendor = {
  name: string;
  category: McpCategory;
  status: string;
  plans: string;
  clients: string;
  writes: string;
  createsJobs: 'Yes' | 'No' | 'Not stated';
  evidence: string;
  note: string;
  source: string;
  /** Our setup guide for this vendor's MCP server, when one exists. */
  guide?: string;
  /** When this row was last re-read, if later than MCP_COMPARISON_REVIEWED. */
  reviewed?: string;
  isBeatview?: boolean;
};

export const MCP_VENDORS: McpVendor[] = [
  {
    name: 'Beatview', isBeatview: true, category: 'Screening & interviews',
    status: 'Live', plans: 'Every plan, including Free',
    clients: 'Claude, ChatGPT, Gemini, Cursor, any MCP client',
    writes: 'Create, update, publish and unpublish jobs; build a job’s AI interview from approved questions; move candidates between stages; send AI interview invitations (confirms first)',
    createsJobs: 'Yes',
    evidence: 'AI resume insights, per-question interview assessments, work style results — recordings stay in Beatview',
    note: '16 actions; 60 calls a minute and 5,000 a day per connection; every action logged.',
    source: 'https://platform.beatview.ai/docs/integrations/mcp-server',
  },
  {
    name: 'Greenhouse', category: 'ATS',
    status: 'Open beta (since July 2026)', plans: 'Core, Plus and Pro',
    clients: 'Claude, ChatGPT, Google Antigravity, Amazon Q, Glean, Grok; others once an admin allows them',
    writes: 'Notes, stage moves, candidate updates and scorecards, within scopes a Site Admin allows; rejecting or hiring needs a person to confirm',
    createsJobs: 'Not stated',
    evidence: 'Scorecards, plus Greenhouse Notetaker transcripts and recording links',
    note: 'Admins set the scopes in Dev Center → MCP Access. Hosted in the US only; every delete is blocked.',
    source: 'https://support.greenhouse.io/hc/en-us/articles/52090956642075-Greenhouse-MCP',
    guide: '/guides/greenhouse-mcp', reviewed: '2026-10-01',
  },
  {
    name: 'Workable', category: 'ATS',
    status: 'Generally available (May 2026)', plans: 'No extra cost; advanced search on Premier+ and Enterprise',
    clients: 'Claude, ChatGPT, Cursor, any MCP client',
    writes: '94 tools: move, disqualify and update candidates, approve offers, create requisitions, manage members, log time off',
    createsJobs: 'No',
    evidence: 'Not stated',
    note: '94 tools since July 2026 (38 at launch), covering HR data too. Access follows each user’s role and assigned jobs.',
    source: 'https://resources.workable.com/workable-mcp-server',
    guide: '/guides/workable-mcp', reviewed: '2026-10-01',
  },
  {
    name: 'Ashby', category: 'ATS',
    status: 'Open beta (since June 2026)', plans: 'Foundations, Legacy Plus, Plus and Enterprise (not Analytics Only)',
    clients: 'ChatGPT, Claude, Cursor, Glean, Gemini CLI, Slack, any MCP client',
    writes: 'Create candidate, draft notes for you to confirm, change application stage, consider a candidate for a job',
    createsJobs: 'No',
    evidence: 'Interview feedback, transcripts, your own scorecards and interview prep briefings',
    note: 'An Org Admin switches it on; 120 requests and 120 tool-budget units a minute per user.',
    source: 'https://docs.ashbyhq.com/ashby-mcp-server-beta',
    guide: '/guides/ashby-mcp', reviewed: '2026-10-01',
  },
  {
    name: 'Zoho Recruit', category: 'ATS',
    status: 'Live, via Zoho MCP', plans: 'Not stated (page says “get started for free”)',
    clients: 'Claude, ChatGPT, Gemini, Cursor, Windsurf, VS Code',
    writes: 'Update candidate stages and records',
    createsJobs: 'Not stated',
    evidence: 'Not stated',
    note: 'Serves in-house HR teams and recruitment agencies; follows existing Zoho roles and permissions.',
    source: 'https://www.zoho.com/recruit/mcp.html',
  },
  {
    name: 'Pinpoint', category: 'ATS',
    status: 'Live (version 2, June 2026)', plans: 'Not stated',
    clients: 'Any MCP client; one-click setup in Claude Desktop',
    writes: 'Move candidates, draft outreach, create and manage jobs, interviews, notifications and dashboards',
    createsJobs: 'Yes',
    evidence: 'Not stated',
    note: 'Honours each user’s Pinpoint permissions; activity is logged.',
    source: 'https://www.pinpointhq.com/features/mcp',
  },
  {
    name: 'Manatal', category: 'Agency CRM',
    status: 'Live; some features in development', plans: 'Enterprise Plus',
    clients: 'Claude (recommended), Gemini CLI, Copilot Studio, ChatGPT via its app directory',
    writes: '28 tools: create candidates, jobs and organisations, add notes, add a candidate to a job',
    createsJobs: 'Yes',
    evidence: 'Not stated',
    note: 'ATS and recruitment CRM; the server URL itself is treated as a credential.',
    source: 'https://support.manatal.com/docs/mcp-server',
  },
  {
    name: 'Recruitly', category: 'Agency CRM',
    status: 'Live', plans: 'Every Recruitly plan (Claude Pro or above required)',
    clients: 'Claude only (web, desktop, Claude Code)',
    writes: 'None — read-only',
    createsJobs: 'No',
    evidence: 'Not stated',
    note: 'Covers candidates, jobs, contacts, billing, placements and team performance.',
    source: 'https://recruitly.io/mcp',
  },
  {
    name: 'Leonar', category: 'Agency CRM',
    status: 'Live', plans: 'Professional, Enterprise and eligible trials',
    clients: 'Claude, ChatGPT, Cursor, custom agents',
    writes: 'Log notes, create or update contacts, move candidates, prepare and enrol outreach sequences',
    createsJobs: 'Not stated',
    evidence: 'Not stated',
    note: 'Focused on sourcing, enrichment and outreach.',
    source: 'https://www.leonar.app/mcp/',
  },
  {
    name: 'PCRecruiter', category: 'Agency CRM',
    status: 'Live', plans: 'Not stated',
    clients: 'Claude, ChatGPT',
    writes: 'Record updates, searches and reports through conversation',
    createsJobs: 'Not stated',
    evidence: 'Not stated',
    note: 'Recruiting and staffing database; few technical details published.',
    source: 'https://www.pcrecruiter.net/site/features/mcp/',
  },
];

export const MCP_COMPARISON_FAQS = [
  { question: 'Which ATS platforms have an MCP server?', answer: 'As of October 2026, Greenhouse (open beta), Workable, Ashby (open beta), Zoho Recruit, Pinpoint and Manatal publish official MCP servers. Agency CRMs Recruitly, Leonar and PCRecruiter do too, and Beatview offers one for AI screening and interviews. Workday offers MCP access through Agent-Ready Tools, in early access. Bullhorn and Lever have no official MCP server, and Bullhorn’s API policy requires its written permission before MCPs or AI tools connect to its API.' },
  { question: 'Which recruiting MCP server is best for recruitment agencies?', answer: 'For agencies that want candidates screened and interviewed before anyone asks, Beatview. Its server covers job publishing, AI resume screening results, AI interview assessments and interview invitations, on every plan including Free, and it runs alongside the ATS you keep. Of the agency CRMs, Manatal’s server requires the Enterprise Plus plan and Recruitly’s is read-only and works with Claude only.' },
  { question: 'What is the difference between an ATS MCP server and Beatview’s?', answer: 'An ATS server gives an assistant your records — candidates, jobs, stages and notes — so it can search and update them. Beatview’s server also returns work already done on those candidates: AI resume screening against your criteria and structured interview assessments, question by question. Many teams connect both.' },
  { question: 'Do I need a paid AI assistant plan?', answer: 'It depends on the assistant. Claude allows one custom connector on its free plan. OpenAI documents custom MCP apps with write actions for ChatGPT Business, Enterprise and Edu. Gemini currently offers custom apps to personal Google Accounts in the US. Each vendor’s own plan rules also apply — see the Plans column.' },
  { question: 'Is it safe to connect candidate data to an AI assistant?', answer: 'Check four things: that each user connects with their own login and sees only what they already can; which actions can write, and whether the assistant must confirm them; whether an admin can see and revoke connections; and whether your organisation has approved the AI provider for candidate data.' },
  { question: 'How often is this comparison updated?', answer: 'Each row was read from the vendor’s own page on the date shown at the top. MCP servers change quickly, so check the linked source before you decide. Vendors can contact Beatview to correct a row.' },
] as const;

/* ───────────── Capability scorecard ───────────── */
// `true` = documented on the vendor page we read; a string = partly, in the vendor's own terms;
// `false` = not documented on that page (which is not proof the feature is missing).

export const MATRIX_VENDORS = ['Beatview', 'Greenhouse', 'Workable', 'Ashby', 'Manatal'] as const;
export type MatrixValue = boolean | string;

export const MCP_MATRIX: { capability: string; values: MatrixValue[] }[] = [
  { capability: 'Returns AI resume screening results', values: [true, false, false, 'Resume file only', false] },
  { capability: 'Returns AI interview assessments', values: [true, 'Human scorecards', false, 'Human feedback', false] },
  { capability: 'Sends AI interview invitations', values: [true, false, false, false, false] },
  { capability: 'Creates and publishes jobs', values: [true, false, false, false, 'Creates jobs'] },
  { capability: 'Confirms with you before candidate-facing actions', values: [true, 'Admin scopes', false, 'Notes as drafts', false] },
  { capability: 'Interview recordings stay in the platform', values: [true, false, false, 'Transcripts shared', false] },
  { capability: 'Tells the assistant how to lay out results', values: [true, false, false, false, false] },
  { capability: 'Included on a free plan', values: [true, 'Core, Plus, Pro', 'Paid plans, no extra cost', 'All plans (beta)', 'Enterprise Plus'] },
  { capability: 'Names Claude, ChatGPT and Gemini', values: [true, 'Claude, ChatGPT', 'Claude, ChatGPT', 'Claude, ChatGPT, Gemini CLI', 'Claude, ChatGPT, Gemini CLI'] },
  { capability: 'Generally available', values: [true, 'Open beta', true, 'Open beta', 'Partly in development'] },
];

/** Beatview's answers to the evaluation checklist on the comparison page. */
export const BEATVIEW_ANSWERS: [string, string][] = [
  ['Does each person connect with their own login?', 'Yes. Every connection is tied to one Beatview account and needs the user to sign in and approve the agent explicitly.'],
  ['Which actions can write, and are they confirmed?', 'Only two affect candidates — moving a stage and sending an interview invitation — and the server tells the assistant to confirm both with you first.'],
  ['Can an admin see and revoke connections?', 'Yes. Every action is logged in Integrations → MCP Server → Settings, with today’s call count, and a connection can be revoked instantly.'],
  ['Which plan do you need?', 'Any. The MCP server is included on every Beatview plan, including Free.'],
  ['Which assistants are named?', 'Claude, ChatGPT, Gemini and Cursor, plus any MCP-compatible tool.'],
  ['What leaves the platform?', 'Interview recordings never do. Answers come with Beatview’s assessment and a link for signed-in members; resume files come as a link that expires after an hour.'],
  ['Is there a rate limit?', 'Yes: 60 calls a minute and 5,000 a day per connection, so a runaway agent cannot hammer your account.'],
];
